import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { PropertyUnit } from "@/models/properties.model";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const formSchema = z.object({
  unit_number: z.string().min(1, "Unit number is required"),
  floor_plan: z.string().optional(),
  beds: z.coerce.number().min(0, "Beds must be ≥ 0"),
  baths: z.coerce.number().min(0, "Baths must be ≥ 0"),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FormValues) => void;
  unit?: PropertyUnit | null;
}

export function UnitModal({ open, onOpenChange, onSubmit, unit }: Props) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      unit_number: "",
      floor_plan: "",
      beds: 0,
      baths: 0,
    },
    mode: "onSubmit",
  });

  // When `unit` changes, reset the form
  useEffect(() => {
    if (unit) {
      form.reset({
        unit_number: unit.unit_number,
        floor_plan: unit.floor_plan || "",
        beds: unit.beds,
        baths: unit.baths,
      });
    } else if (!open) {
      // Reset form when modal closes
      form.reset({
        unit_number: "",
        floor_plan: "",
        beds: 0,
        baths: 0,
      });
    }
  }, [unit, form, open]);

  const fields: FormFieldDef[] = [
    {
      type: "text",
      name: "unit_number",
      label: "Unit Number",
      required: true,
    },
    {
      type: "text",
      name: "floor_plan",
      label: "Floor Plan",
      required: false,
    },
    {
      type: "number",
      name: "beds",
      label: "Beds",
      required: true,
    },
    {
      type: "number",
      name: "baths",
      label: "Baths",
      required: true,
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    formDefinition: fields,
    buttonText: unit ? "Update Unit" : "Create Unit",
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{unit ? "Edit Unit" : "Add Unit"}</SheetTitle>
          <SheetDescription>
            <FormCreator {...formCreatorProps} />
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
